/** "Offer a container": a forwarder's box with dates and a price for Dockie's customers.
 *  It's listed only after Dockie's yes; customers see how many cars joined, never whose. */
import React, { useState } from "react";
import { useMyOffers, useOfferContainer } from "@/services/python-api/customerHooks";
import { Card, Empty, Loading, Page, Pill, day } from "../customer/parts";

const STATE = {
  proposed: ["Waiting for Dockie", "bg-[#FEF6E7] text-[#865503]"],
  open: ["Listed", "bg-[#E7F6EC] text-[#036B26]"],
  closed: ["Closed", "bg-[#F0F2F5] text-[#344054]"],
};

export default function OfferContainer() {
  const { data, isLoading, isError } = useMyOffers();
  const offer = useOfferContainer();
  const [f, setF] = useState({ origin: "Houston", method: "consolidated", size: "40HC", slots: 4, cut_off: "", sails: "", line: "", price: "" });
  const origins = data?.origins ?? ["Houston", "Los Angeles", "Jacksonville/Savannah", "Baltimore"];
  const input = (key, label, type = "text", placeholder = "") => (
    <label className="block">
      <span className="text-[13px] font-medium text-[#344054]">{label}</span>
      <input
        type={type}
        value={f[key]}
        placeholder={placeholder}
        onChange={(e) => setF({ ...f, [key]: e.target.value })}
        className="mt-1.5 h-11 w-full rounded-xl border border-[#D0D5DD] px-3 text-[15px] outline-none focus:border-[#2563EB]"
      />
    </label>
  );
  if (isError) return <Page title="Offer a container"><Empty title="For forwarders only" /></Page>;
  return (
    <Page title="Offer a container">
      <Card>
        <p className="text-[16px] font-semibold text-[#101928]">A box for Dockie's customers</p>
        <p className="mt-0.5 text-[13px] text-[#667185]">Dockie checks the dates and price before customers see it.</p>
        <p className="mt-4 text-[13px] font-medium text-[#344054]">Loading from</p>
        <div className="mt-1.5 flex flex-wrap gap-2">
          {origins.map((o) => (
            <button
              key={o}
              onClick={() => setF({ ...f, origin: o })}
              className={`rounded-full border px-3 py-1.5 text-[13px] ${
                f.origin === o ? "border-[#2563EB] bg-[#EFF6FF] text-[#1D4ED8]" : "border-[#E4E7EC] text-[#344054]"
              }`}
            >
              {o}
            </button>
          ))}
        </div>
        <div className="mt-4 grid grid-cols-2 gap-2">
          {[
            ["consolidated", "Shared", "Price per car"],
            ["fcl", "Full container", "Price for the box"],
          ].map(([k, label, what]) => (
            <button
              key={k}
              onClick={() => setF({ ...f, method: k })}
              className={`rounded-2xl border p-3 text-left ${f.method === k ? "border-[#2563EB] bg-[#EFF6FF]" : "border-[#E4E7EC]"}`}
            >
              <span className="block text-[14px] font-semibold text-[#101928]">{label}</span>
              <span className="block text-[12px] text-[#667185]">{what}</span>
            </button>
          ))}
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3">
          {input("cut_off", "Cut-off", "date")}
          {input("sails", "Sails", "date")}
          {input("line", "Line", "text", "Grimaldi")}
          {input("price", f.method === "fcl" ? "Price for the box ($)" : "Price per car ($)", "number", "1200")}
        </div>
        {offer.error ? <p className="mt-3 text-[13px] text-[#CB1A14]">{offer.error.response?.data?.detail}</p> : null}
        <button
          disabled={offer.isPending}
          onClick={() => offer.mutate({ ...f, slots: Number(f.slots), cut_off: f.cut_off || null, sails: f.sails || null })}
          className="mt-5 h-11 w-full rounded-full bg-[#2563EB] text-[15px] font-medium text-white disabled:opacity-60"
        >
          {offer.isSuccess ? "Sent: offer another" : "Send to Dockie"}
        </button>
      </Card>
      <p className="mb-2 mt-6 px-1 text-[13px] font-medium text-[#667185]">Your offers</p>
      {isLoading ? (
        <Loading />
      ) : (
        <div className="space-y-2">
          {(data?.offers ?? []).map((o) => (
            <Card key={o.id} className="flex items-center justify-between gap-3">
              <div>
                <p className="text-[14px] font-semibold text-[#101928]">
                  {o.size} from {o.origin} · {o.price || "no price"}
                </p>
                <p className="text-[12px] text-[#667185]">
                  Cut-off {day(o.cut_off) || "not set"} · {o.assigned} of {o.slots} cars joined
                </p>
              </div>
              <Pill tone={STATE[o.state][1]}>{STATE[o.state][0]}</Pill>
            </Card>
          ))}
        </div>
      )}
    </Page>
  );
}
